"use client";

/**
 * "Forgot Password" — step one of two.
 *
 * Takes an email address and asks auth-gateway to send a reset link, then shows
 * a **"check your email"** state. That is the whole modal: the reset itself
 * happens on `/auth/reset-password`, opened from the emailed link.
 *
 * ⚠️ **This used to render an OTP-entry step against an endpoint that never
 * existed.** auth-gateway's flow is request → emailed link → confirm, with no
 * code the user types
 * (`docs/pos-api/api-req/answers/DONE-2026-08-05-password-reset-paths-answers.md`).
 * Do not reintroduce a code input, and do not port one from a sibling app.
 *
 * **The response cannot tell a registered address from an unregistered one** —
 * it is always 200 with the same sentence, deliberately, so that a login screen
 * cannot be used to test which addresses hold accounts. So this never renders
 * "no account found", and the confirmation copy is shown verbatim.
 */
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { RESEND_OTP_INTERVAL_SECONDS } from "@/config";
import { useAuth } from "@/hooks/useAuth";
import CButton from "@/Shared/CButton/CButton";
import CModal from "@/Shared/CModal/CModal";
import RequestStep from "./RequestStep";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialEmail?: string;
}

const ResetPasswordModal: React.FC<Props> = ({
  isOpen,
  onClose,
  initialEmail,
}) => {
  const { requestPasswordReset } = useAuth();

  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [loading, setLoading] = useState(false);

  // Initialize / reset state when the modal opens or closes
  useEffect(() => {
    if (isOpen && initialEmail) setEmail(initialEmail);
    if (!isOpen) {
      setSent(false);
      setEmail("");
      setMessage(null);
      setSecondsRemaining(0);
    }
  }, [isOpen, initialEmail]);

  // Tick the resend countdown
  useEffect(() => {
    if (secondsRemaining <= 0) return;
    const timer = setInterval(() => {
      setSecondsRemaining((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsRemaining]);

  /**
   * Ask for the email. Used for both the first send and a resend — they are the
   * same call, and the countdown exists because the gateway allows only
   * **5 per minute per address** and fails closed.
   */
  const handleRequest = useCallback(async () => {
    if (secondsRemaining > 0) return;
    setLoading(true);
    try {
      const result = await requestPasswordReset(email);
      setMessage(
        result.message ??
          "If an account exists for that address, a reset link has been sent.",
      );
      // A rate-limit refusal is the one case that must NOT advance — the user
      // has something to act on (waiting), unlike the uniform 200
      if (result.success) {
        setSent(true);
        setSecondsRemaining(RESEND_OTP_INTERVAL_SECONDS);
      }
    } finally {
      setLoading(false);
    }
  }, [email, requestPasswordReset, secondsRemaining]);

  return (
    <CModal
      isOpen={isOpen}
      onClose={onClose}
      title={sent ? "Check your email" : "Reset Password"}
    >
      <div className="space-y-4">
        {sent ? (
          <div className="space-y-4">
            <p className="paragraph-sm text-text-secondary">{message}</p>
            <p className="paragraph-sm text-text-tertiary">
              Open the link in that email to choose a new password. It expires
              in 30 minutes.
            </p>

            <div className="flex items-center justify-between gap-3">
              <CButton
                variant="link"
                size="sm"
                disabled={secondsRemaining > 0 || loading}
                onClick={handleRequest}
              >
                {secondsRemaining > 0
                  ? `Resend in ${secondsRemaining}s`
                  : "Resend link"}
              </CButton>
              <CButton variant="outline" size="sm" onClick={onClose}>
                Done
              </CButton>
            </div>
          </div>
        ) : (
          <>
            {message && (
              <p className="paragraph-sm text-error-500">{message}</p>
            )}
            <RequestStep
              email={email}
              setEmail={setEmail}
              onRequest={handleRequest}
              loading={loading}
            />
          </>
        )}
      </div>
    </CModal>
  );
};

export default ResetPasswordModal;
